import { SearchCriteriaFilterActions } from '@corratech/pylot-filters-and-sort'
import type {
    OutputInterface,
    ProductVariablesInterface
} from '@pylot-data/api/operations/get-product'
import { productDetailQuery } from '@pylot-data/hooks/product/graphql/productDetailQuery'
import { SwrUseQueryOutput, useQuery } from '@pylot-data/hooks/use-query'
import { GiftCardTypeEnum } from '@pylot-data/enums/GiftCardTypeEnum.d'
import { RequestInit } from '@vercel/fetch'
import { SWRConfiguration } from 'swr'

export enum ProductType {
    CONFIGURABLE_PRODUCT = 'ConfigurableProduct',
    SIMPLE_PRODUCT = 'SimpleProduct',
    GROUPED_PRODUCT = 'GroupedProduct',
    GIFT_CARD_PRODUCT = 'GiftCardProduct',
    DOWNLOADABLE_PRODUCT = 'DownloadableProduct',
    BUNDLE_PRODUCT = 'BundleProduct',
    VIRTUAL_PRODUCT = 'VirtualProduct'
}

//Define the current supported product type
const SUPPORTED_PRODUCT_TYPES = [
    ProductType.SIMPLE_PRODUCT,
    ProductType.CONFIGURABLE_PRODUCT,
    ProductType.GIFT_CARD_PRODUCT,
    ProductType.BUNDLE_PRODUCT
]

export type UseProductFetchReturn<ProductType> = {
    product: ProductType
    isSupportedProductType: boolean
    isConfig: boolean
    isGiftCard: boolean
    isBundle: boolean
    isSimple: boolean
} & SwrUseQueryOutput<OutputInterface>

export type UseProductFetch<ProductType> = (
    productOptions: {
        productUrl: string
        productQuery?: string | undefined
    },
    swrOptions?: SWRConfiguration,
    fetchOptions?: RequestInit
) => UseProductFetchReturn<ProductType>

export const useProductFetch: UseProductFetch<any> = (
    { productUrl, productQuery = productDetailQuery },
    swrOptions,
    fetchOptions
) => {
    // useQuery to fetch data
    const SWRResult = useQuery<ProductVariablesInterface, OutputInterface>(
        productQuery,
        {
            filter: {
                url_key: {
                    eq: productUrl
                }
            }
        },
        swrOptions,
        fetchOptions
    )

    const product = SWRResult.data?.data?.productDetail?.items[0]!

    const productType = product?.__typename as ProductType | undefined
    const isConfig = productType === ProductType.CONFIGURABLE_PRODUCT
    const isGiftCard =
        productType === ProductType.GIFT_CARD_PRODUCT &&
        product?.giftcard_type === GiftCardTypeEnum.Virtual
    const isBundle = productType === ProductType.BUNDLE_PRODUCT
    const isSimple = productType === ProductType.SIMPLE_PRODUCT
    //check if the product type is supported to render the product page
    const isSupportedProductType = SUPPORTED_PRODUCT_TYPES.includes(
        productType as ProductType
    )

    return {
        product,
        isSupportedProductType,
        isConfig,
        isGiftCard,
        isBundle,
        isSimple,
        ...SWRResult
    }
}
